import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'

function MessagingSystem({ currentUser, userRole, onClose }) {
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [staffList, setStaffList] = useState([])
  const [showNewChat, setShowNewChat] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [unreadCounts, setUnreadCounts] = useState({})
  const messagesEndRef = useRef(null)

  // Set up realtime subscription for new messages
  useEffect(() => {
    if (!currentUser?.id) return

    const channel = supabase
      .channel('messages-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        (payload) => {
          const newMsg = payload.new
          
          if (selectedConversation && newMsg.conversation_id === selectedConversation.id) {
            setMessages(prev => [...prev, newMsg])
            scrollToBottom()
            
            if (newMsg.sender_id !== currentUser.id) {
              markSingleMessageAsRead(newMsg.id)
            }
          }
          
          setConversations(prev => prev.map(conv => 
            conv.id === newMsg.conversation_id 
              ? { 
                  ...conv, 
                  last_message: newMsg.message, 
                  last_message_time: newMsg.created_at,
                  updated_at: newMsg.created_at 
                }
              : conv
          ))
          
          if (newMsg.sender_id !== currentUser.id && 
              (!selectedConversation || newMsg.conversation_id !== selectedConversation.id)) {
            setUnreadCounts(prev => ({
              ...prev,
              [newMsg.conversation_id]: (prev[newMsg.conversation_id] || 0) + 1
            }))
          }
          
          fetchConversations()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [selectedConversation, currentUser?.id])

  useEffect(() => {
    if (currentUser?.id) {
      fetchConversations()
      fetchStaffList()
    }
  }, [currentUser?.id])

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id)
      markMessagesAsRead(selectedConversation.id)
    }
  }, [selectedConversation])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const fetchConversations = async () => {
    setLoading(true)
    
    try {
      const { data: participantData, error: participantError } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', currentUser.id)

      if (participantError) throw participantError
      
      if (!participantData || participantData.length === 0) {
        setConversations([])
        setLoading(false)
        return
      }

      const conversationIds = participantData.map(p => p.conversation_id)

      const { data: convoData, error: convoError } = await supabase
        .from('conversations')
        .select('*')
        .in('id', conversationIds)
        .order('updated_at', { ascending: false })

      if (convoError) throw convoError
      
      setConversations(convoData || [])
      
      for (const convo of (convoData || [])) {
        await fetchLastMessage(convo.id)
        await fetchUnreadCount(convo.id)
      }
    } catch (err) {
      console.error('Error fetching conversations:', err)
    }
    
    setLoading(false)
  }

  const fetchLastMessage = async (conversationId) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('message, created_at, sender_id')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      
      if (!error && data) {
        setConversations(prev => prev.map(c => 
          c.id === conversationId 
            ? { 
                ...c, 
                last_message: data.message, 
                last_message_time: data.created_at,
                last_sender_id: data.sender_id
              } 
            : c
        ))
      }
    } catch (err) {
      console.error('Error fetching last message:', err)
    }
  }

  const fetchUnreadCount = async (conversationId) => {
    try {
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', currentUser.id)
        .eq('is_read', false)

      if (!error && count && count > 0) {
        setUnreadCounts(prev => ({ ...prev, [conversationId]: count }))
      }
    } catch (err) {
      console.error('Error fetching unread count:', err)
    }
  }

  const markMessagesAsRead = async (conversationId) => {
    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', currentUser.id)
        .eq('is_read', false)

      if (!error) {
        setUnreadCounts(prev => ({ ...prev, [conversationId]: 0 }))
      }
    } catch (err) {
      console.error('Error marking messages as read:', err)
    }
  }

  const markSingleMessageAsRead = async (messageId) => {
    try {
      await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('id', messageId)
    } catch (err) {
      console.error('Error marking message as read:', err)
    }
  }

  const fetchMessages = async (conversationId) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:sender_id (id, email, full_name)
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true })

      if (error) throw error
      if (data) {
        setMessages(data)
      }
    } catch (err) {
      console.error('Error fetching messages:', err)
      setMessages([])
    }
  }

  const fetchStaffList = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, full_name, role')
        .neq('id', currentUser.id)
        .limit(50)
      
      if (error) throw error
      if (data) setStaffList(data)
    } catch (err) {
      console.error('Error fetching staff:', err)
    }
  }

  const createConversation = async () => {
    if (selectedStaff.length === 0) {
      alert('Please select at least one person')
      return
    }

    try {
      const { data: convoData, error: convoError } = await supabase
        .from('conversations')
        .insert({
          is_group: selectedStaff.length > 1,
          conversation_name: selectedStaff.length === 1 ? (selectedStaff[0].full_name || selectedStaff[0].email) : `${selectedStaff.length} participants`,
          created_by: currentUser.id,
          updated_at: new Date().toISOString()
        })
        .select()
        .single()

      if (convoError) throw convoError

      const participants = [
        { conversation_id: convoData.id, user_id: currentUser.id },
        ...selectedStaff.map(staff => ({ conversation_id: convoData.id, user_id: staff.id }))
      ]

      const { error: partError } = await supabase
        .from('conversation_participants')
        .insert(participants)

      if (partError) throw partError

      setSelectedConversation(convoData)
      setShowNewChat(false)
      setSelectedStaff([])
      fetchConversations()
    } catch (err) {
      console.error('Error creating conversation:', err)
      alert('Error creating conversation: ' + err.message)
    }
  }

  const sendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || sending || !selectedConversation) return

    setSending(true)
    try {
      const messageData = {
        conversation_id: selectedConversation.id,
        sender_id: currentUser.id,
        message: newMessage.trim(),
        is_read: false
      }
      
      const { error } = await supabase
        .from('messages')
        .insert(messageData)

      if (error) throw error
      
      setNewMessage('')
      
      await supabase
        .from('conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', selectedConversation.id)
      
      fetchConversations()
      
    } catch (err) {
      console.error('Error sending message:', err)
      alert('Error sending message: ' + err.message)
    }
    setSending(false)
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    const now = new Date()
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    return date.toLocaleDateString()
  }

  // Get color based on user role for incoming messages
  const getSenderColor = (role) => {
    const colors = {
      leadership: '#1e3a5f',
      accounting: '#059669',
      marketing: '#d97706',
      legal: '#7c3aed',
      recruitment: '#ec4899',
      agent: '#0891b2',
      default: '#64748b'
    }
    return colors[role] || colors.default
  }

  return (
    <div style={styles.container}>
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <h3 style={{ margin: 0 }}>Messages</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setShowNewChat(true)} style={styles.newChatBtn}>
              + New
            </button>
            <button onClick={onClose} style={styles.closeBtn}>
              ✕
            </button>
          </div>
        </div>

        <div style={styles.conversationsList}>
          {loading ? (
            <div style={styles.loading}>Loading conversations...</div>
          ) : conversations.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>💬</div>
              <p>No messages yet</p>
              <button onClick={() => setShowNewChat(true)} style={styles.startChatBtn}>
                Start a conversation
              </button>
            </div>
          ) : (
            conversations.map(convo => (
              <div
                key={convo.id}
                onClick={() => setSelectedConversation(convo)}
                style={{
                  ...styles.conversationItem,
                  ...(selectedConversation?.id === convo.id ? styles.selectedConversation : {})
                }}
              >
                <div style={styles.avatar}>
                  {convo.is_group ? '👥' : '👤'}
                </div>
                <div style={styles.conversationInfo}>
                  <div style={styles.conversationName}>
                    {convo.conversation_name || 'Chat'}
                  </div>
                  <div style={styles.lastMessage}>
                    {convo.last_message?.substring(0, 35) || 'No messages yet'}
                  </div>
                </div>
                {unreadCounts[convo.id] > 0 && (
                  <div style={styles.unreadBadge}>{unreadCounts[convo.id]}</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div style={styles.chatArea}>
        {selectedConversation ? (
          <>
            <div style={styles.chatHeader}>
              <h3 style={{ margin: 0 }}>{selectedConversation.conversation_name || 'Chat'}</h3>
            </div>

            <div style={styles.messagesArea}>
              {messages.length === 0 ? (
                <div style={styles.noMessages}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>💬</div>
                  <p>No messages yet. Say hello!</p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isMyMessage = msg.sender_id === currentUser.id
                  const senderRole = msg.sender?.role || 'default'
                  const senderColor = getSenderColor(senderRole)
                  
                  return (
                    <div
                      key={msg.id || index}
                      style={{
                        ...styles.message,
                        ...(isMyMessage ? styles.myMessage : styles.otherMessage)
                      }}
                    >
                      {!isMyMessage && (
                        <div style={{ 
                          ...styles.messageSender, 
                          color: senderColor,
                          fontWeight: '600'
                        }}>
                          {msg.sender?.full_name || msg.sender?.email?.split('@')[0] || 'User'}
                          <span style={{ 
                            marginLeft: '8px', 
                            fontSize: '10px', 
                            backgroundColor: senderColor + '20',
                            color: senderColor,
                            padding: '2px 6px',
                            borderRadius: '12px'
                          }}>
                            {senderRole}
                          </span>
                        </div>
                      )}
                      <div style={{
                        ...styles.messageBubble,
                        ...(isMyMessage ? styles.myMessageBubble : { ...styles.otherMessageBubble, backgroundColor: senderColor + '15', borderLeftColor: senderColor })
                      }}>
                        {msg.message}
                        <div style={styles.messageTime}>
                          {formatTime(msg.created_at)}
                          {isMyMessage && (
                            <span style={styles.messageStatus}>
                              {msg.is_read ? '✓✓' : '✓'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={sendMessage} style={styles.messageForm}>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                style={styles.messageInput}
              />
              <button type="submit" disabled={sending} style={styles.sendBtn}>
                {sending ? '...' : 'Send'}
              </button>
            </form>
          </>
        ) : (
          <div style={styles.noConversation}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>💬</div>
            <h3>Select a conversation</h3>
            <p>Choose a chat from the sidebar to start messaging</p>
          </div>
        )}
      </div>

      {showNewChat && (
        <div style={styles.modalOverlay} onClick={() => setShowNewChat(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '16px' }}>New Conversation</h3>
            <div style={styles.staffList}>
              {staffList.length === 0 ? (
                <div style={styles.loading}>Loading staff...</div>
              ) : (
                staffList.map(staff => (
                  <label key={staff.id} style={styles.staffItem}>
                    <input
                      type="checkbox"
                      checked={selectedStaff.some(s => s.id === staff.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStaff([...selectedStaff, staff])
                        } else {
                          setSelectedStaff(selectedStaff.filter(s => s.id !== staff.id))
                        }
                      }}
                      style={{ marginRight: '12px' }}
                    />
                    <div>
                      <div style={styles.staffName}>{staff.full_name || staff.email}</div>
                      <div style={styles.staffRole}>{staff.role || 'Staff'}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
            <div style={styles.modalButtons}>
              <button onClick={() => setShowNewChat(false)} style={styles.cancelBtn}>
                Cancel
              </button>
              <button onClick={createConversation} style={styles.createBtn}>
                Start Chat ({selectedStaff.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  container: {
    display: 'flex',
    width: '800px',
    height: '600px',
    background: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    fontFamily: "'Inter', system-ui, sans-serif"
  },
  sidebar: {
    width: '280px',
    borderRight: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    background: '#f8fafc'
  },
  sidebarHeader: {
    padding: '16px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  newChatBtn: {
    background: '#059669',
    color: '#fff',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  },
  closeBtn: {
    background: '#ef4444',
    color: '#fff',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  },
  conversationsList: {
    flex: 1,
    overflowY: 'auto'
  },
  conversationItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    cursor: 'pointer',
    borderBottom: '1px solid #e2e8f0',
    position: 'relative',
    transition: 'background 0.2s'
  },
  selectedConversation: {
    background: '#e0f2fe'
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: '#1e3a5f',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    fontSize: '18px'
  },
  conversationInfo: {
    flex: 1,
    minWidth: 0
  },
  conversationName: {
    fontWeight: '600',
    marginBottom: '4px',
    fontSize: '14px'
  },
  lastMessage: {
    fontSize: '12px',
    color: '#64748b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  unreadBadge: {
    background: '#059669',
    color: '#fff',
    borderRadius: '50%',
    minWidth: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: '600',
    padding: '0 4px'
  },
  chatArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#fff'
  },
  chatHeader: {
    padding: '16px',
    borderBottom: '1px solid #e2e8f0',
    background: '#fff'
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '75%'
  },
  myMessage: {
    alignSelf: 'flex-end'
  },
  otherMessage: {
    alignSelf: 'flex-start'
  },
  messageSender: {
    fontSize: '12px',
    marginBottom: '4px',
    marginLeft: '12px'
  },
  messageBubble: {
    padding: '10px 14px',
    borderRadius: '18px',
    fontSize: '14px',
    wordWrap: 'break-word',
    maxWidth: '100%',
    borderLeft: '3px solid',
    boxShadow: '0 1px 1px rgba(0,0,0,0.05)'
  },
  myMessageBubble: {
    background: '#1e3a5f',
    color: '#fff',
    borderRadius: '18px 4px 18px 18px',
    borderLeft: '3px solid #059669'
  },
  otherMessageBubble: {
    background: '#f1f5f9',
    color: '#1e293b',
    borderRadius: '4px 18px 18px 18px'
  },
  messageTime: {
    fontSize: '10px',
    marginTop: '4px',
    display: 'flex',
    gap: '4px',
    justifyContent: 'flex-end'
  },
  messageStatus: {
    fontSize: '10px',
    color: '#059669'
  },
  messageForm: {
    padding: '16px',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    gap: '12px'
  },
  messageInput: {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '20px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit'
  },
  sendBtn: {
    background: '#059669',
    color: '#fff',
    border: 'none',
    padding: '8px 20px',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  noConversation: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
    textAlign: 'center'
  },
  noMessages: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
    textAlign: 'center'
  },
  loading: {
    textAlign: 'center',
    padding: '20px',
    color: '#64748b'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#64748b'
  },
  startChatBtn: {
    marginTop: '12px',
    padding: '8px 16px',
    background: '#059669',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    width: '400px',
    maxWidth: '90%'
  },
  staffList: {
    maxHeight: '300px',
    overflowY: 'auto',
    margin: '16px 0'
  },
  staffItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    cursor: 'pointer',
    borderBottom: '1px solid #e2e8f0'
  },
  staffName: {
    fontWeight: '500',
    fontSize: '14px'
  },
  staffRole: {
    fontSize: '11px',
    color: '#64748b'
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '16px'
  },
  cancelBtn: {
    background: '#e2e8f0',
    color: '#334155',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  createBtn: {
    background: '#059669',
    color: '#fff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer'
  }
}

export default MessagingSystem
